import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useUser } from "../context/UserContext";

const Account = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useUser();

  const [activeForm, setActiveForm] = useState("login");
  const [registrationStep, setRegistrationStep] = useState(1);
  const [otpTimer, setOtpTimer] = useState(0);

  const searchParams = new URLSearchParams(location.search);
  const redirectTo = searchParams.get("redirect");

  // State for Login Form
  const [loginData, setLoginData] = useState({
    username: "",
    password: "",
    rememberMe: false,
  });

  // State for Register Form
  const [registerData, setRegisterData] = useState({
    username: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  // OTP State
  const [otpData, setOtpData] = useState({
    otp: "",
    email: "",
    otpSent: false,
    otpVerified: false,
    tempUserId: null,
  });

  // Password visibility states
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Error and loading states
  const [loginErrors, setLoginErrors] = useState({});
  const [registerErrors, setRegisterErrors] = useState({});
  const [otpErrors, setOtpErrors] = useState({});
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isRegisterLoading, setIsRegisterLoading] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [serverMessage, setServerMessage] = useState({ type: "", text: "" });

  const API_URL = "/api";

  // OTP Timer Effect
  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  // Reset registration step when switching to register form
  useEffect(() => {
    if (activeForm === "register") {
      setRegistrationStep(1);
      setOtpData({
        otp: "",
        email: "",
        otpSent: false,
        otpVerified: false,
        tempUserId: null,
      });
    }
  }, [activeForm]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Handle Login Input Changes
  const handleLoginChange = (e) => {
    const { name, value, type, checked } = e.target;
    setLoginData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (loginErrors[name]) {
      setLoginErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle Register Input Changes
  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setRegisterData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (registerErrors[name]) {
      setRegisterErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle OTP Change
  const handleOtpChange = (e) => {
    const { value } = e.target;
    setOtpData((prev) => ({ ...prev, otp: value }));
    if (otpErrors.otp) {
      setOtpErrors((prev) => ({ ...prev, otp: "" }));
    }
  };

  // Validate Login Form
  const validateLogin = () => {
    const errors = {};
    if (!loginData.username.trim()) {
      errors.username = "Username or email is required";
    }
    if (!loginData.password) {
      errors.password = "Password is required";
    }
    setLoginErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate Register Form (Step 1)
  const validateRegister = () => {
    const errors = {};

    if (!registerData.username.trim()) {
      errors.username = "Username is required";
    } else if (registerData.username.length < 3) {
      errors.username = "Username must be at least 3 characters";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+₹/;
    if (!registerData.email) {
      errors.email = "Email is required";
    } else if (!emailRegex.test(registerData.email)) {
      errors.email = "Please enter a valid email address";
    }

    const phoneRegex = /^\d{10,15}₹/;
    if (!registerData.phone) {
      errors.phone = "Phone number is required";
    } else if (!phoneRegex.test(registerData.phone.replace(/[^0-9]/g, ""))) {
      errors.phone = "Please enter a valid phone number (10-15 digits)";
    }

    if (!registerData.password) {
      errors.password = "Password is required";
    } else if (registerData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (!registerData.confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (registerData.password !== registerData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setRegisterErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Send Registration OTP
  const handleSendRegistrationOtp = async () => {
    if (!validateRegister()) return;

    setIsSendingOtp(true);
    setServerMessage({ type: "", text: "" });

    try {
      const response = await axios.post(
        `${API_URL}/auth/send-registration-otp`,
        {
          username: registerData.username,
          email: registerData.email,
          phone: registerData.phone,
          password: registerData.password,
        },
      );

      if (response.data.success) {
        setOtpData({
          otp: "",
          email: registerData.email,
          otpSent: true,
          otpVerified: false,
          tempUserId: response.data.tempUserId,
        });
        setOtpTimer(300);
        setRegistrationStep(2);
        setServerMessage({
          type: "success",
          text: "OTP sent to your email! Please check your inbox.",
        });
      }
    } catch (error) {
      setServerMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          "Failed to send OTP. Please try again.",
      });
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Verify Registration OTP
  const handleVerifyRegistrationOtp = async () => {
    if (!otpData.otp || otpData.otp.length !== 6) {
      setOtpErrors({ otp: "Please enter a valid 6-digit OTP" });
      return;
    }

    setIsRegisterLoading(true);
    setServerMessage({ type: "", text: "" });

    try {
      const response = await axios.post(
        `${API_URL}/auth/verify-registration-otp`,
        {
          email: otpData.email,
          otp: otpData.otp,
          tempUserId: otpData.tempUserId,
        },
      );

      if (response.data.success) {
        setOtpData((prev) => ({ ...prev, otpVerified: true }));
        setServerMessage({
          type: "success",
          text: "Email verified successfully! Registration complete.",
        });

        setTimeout(() => {
          if (redirectTo === "become-seller") {
            navigate("/become-seller"); // ✅ Must be '/become-seller', NOT '/seller/application'
          } else if (response.data.user.role === "seller") {
            navigate("/seller/dashboard");
          } else {
            navigate("/");
          }
        }, 1500);
      }
    } catch (error) {
      setServerMessage({
        type: "error",
        text: error.response?.data?.message || "Invalid OTP. Please try again.",
      });
    } finally {
      setIsRegisterLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (otpTimer > 0) return;

    setIsSendingOtp(true);
    setServerMessage({ type: "", text: "" });

    try {
      const response = await axios.post(
        `${API_URL}/auth/resend-registration-otp`,
        {
          email: otpData.email,
        },
      );

      if (response.data.success) {
        setOtpTimer(300);
        setServerMessage({
          type: "success",
          text: "New OTP sent successfully!",
        });
      }
    } catch (error) {
      setServerMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to resend OTP.",
      });
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Handle Login Submit
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setServerMessage({ type: "", text: "" });

    if (!validateLogin()) return;

    setIsLoginLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        username: loginData.username,
        password: loginData.password,
      });

      if (response.data.success) {
        login(response.data.user, response.data.token, loginData.rememberMe);

        setServerMessage({
          type: "success",
          text: "Login successful! Redirecting...",
        });

        setTimeout(() => {
          // ✅ FIXED: Redirect to become-seller page
          if (redirectTo === "become-seller") {
            navigate("/become-seller");
          } else if (response.data.user.role === "seller") {
            navigate("/seller/dashboard");
          } else {
            navigate("/");
          }
        }, 1500);
      }
    } catch (error) {
      console.error("Login error:", error);
      setServerMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          "Login failed. Please check your credentials.",
      });
    } finally {
      setIsLoginLoading(false);
    }
  };

  return (
    <section
      className="account py-80"
      style={{ minHeight: "100vh", display: "flex", alignItems: "center" }}
    >
      <div className="container container-lg">
        <div className="row justify-content-center">
          <div className="col-xl-5 col-lg-6">
            {/* Server Message */}
            {serverMessage.text && (
              <div
                className={`alert alert-${serverMessage.type === "success" ? "success" : "danger"} mb-4`}
              >
                {serverMessage.text}
              </div>
            )}

            {/* Tab Buttons */}
            <div className="d-flex gap-8 mb-32 border-bottom pb-2">
              <button
                onClick={() => setActiveForm("login")}
                className={`btn btn-link text-lg fw-medium px-0 py-8 ${activeForm === "login" ? "text-main-600 border-bottom border-main-600" : "text-gray-400"}`}
                style={{ textDecoration: "none" }}
              >
                Login
              </button>
              <button
                onClick={() => setActiveForm("register")}
                className={`btn btn-link text-lg fw-medium px-0 py-8 ${activeForm === "register" ? "text-main-600 border-bottom border-main-600" : "text-gray-400"}`}
                style={{ textDecoration: "none" }}
              >
                Create Account
              </button>
            </div>

            {/* ==================== LOGIN FORM ==================== */}
            {activeForm === "login" && (
              <div className="border border-gray-100 rounded-16 px-24 py-40 bg-white shadow-sm">
                <div className="text-center mb-32">
                  <h2 className="fw-semibold mb-8">Welcome Back!</h2>
                  <p className="text-gray-500">
                    Login to your Paudhawala account
                  </p>
                </div>

                <form onSubmit={handleLoginSubmit}>
                  <div className="mb-24">
                    <label className="text-neutral-900 text-lg mb-8 fw-medium">
                      Username or Email <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`common-input ${loginErrors.username ? "border-danger" : ""}`}
                      name="username"
                      placeholder="Enter username or email"
                      value={loginData.username}
                      onChange={handleLoginChange}
                    />
                    {loginErrors.username && (
                      <small className="text-danger">
                        {loginErrors.username}
                      </small>
                    )}
                  </div>

                  {/* Password Field with Eye Icon */}
                  <div className="mb-24">
                    <label className="text-neutral-900 text-lg mb-8 fw-medium">
                      Password <span className="text-danger">*</span>
                    </label>
                    <div className="position-relative">
                      <input
                        type={showLoginPassword ? "text" : "password"}
                        className={`common-input ${loginErrors.password ? "border-danger" : ""}`}
                        name="password"
                        placeholder="Enter Password"
                        value={loginData.password}
                        onChange={handleLoginChange}
                      />
                      <span
                        className={`position-absolute top-50 end-0 me-16 translate-middle-y cursor-pointer ph ${showLoginPassword ? "ph-eye" : "ph-eye-slash"}`}
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                      />
                    </div>
                    {loginErrors.password && (
                      <small className="text-danger">
                        {loginErrors.password}
                      </small>
                    )}
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-32">
                    <div className="form-check d-flex align-items-center">
                      <input
                        type="checkbox"
                        id="rememberMe"
                        name="rememberMe"
                        checked={loginData.rememberMe}
                        onChange={handleLoginChange}
                        className="me-2"
                      />
                      <label
                        className="form-check-label text-gray-600"
                        htmlFor="rememberMe"
                      >
                        Remember me
                      </label>
                    </div>
                    <Link
                      to="/forgot-password"
                      className="text-main-600 text-sm"
                    >
                      Forgot password?
                    </Link>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-main w-100 py-16 rounded-pill fw-semibold"
                    disabled={isLoginLoading}
                  >
                    {isLoginLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>{" "}
                        Logging in...
                      </>
                    ) : (
                      "Login"
                    )}
                  </button>

                  <div className="text-center mt-24">
                    <span className="text-gray-500">New to Paudhawala? </span>
                    <button
                      type="button"
                      onClick={() => setActiveForm("register")}
                      className="btn btn-link p-0 text-main-600 fw-semibold"
                    >
                      Create Account
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* ==================== REGISTER FORM - STEP 1: DETAILS ==================== */}
            {activeForm === "register" && registrationStep === 1 && (
              <div className="border border-gray-100 rounded-16 px-24 py-40 bg-white shadow-sm">
                <div className="text-center mb-32">
                  <h2 className="fw-semibold mb-8">Join Paudhawala</h2>
                  <p className="text-gray-500">
                    Create your account to start shopping
                  </p>
                </div>

                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="mb-24">
                    <label className="text-neutral-900 text-lg mb-8 fw-medium">
                      Username <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`common-input ${registerErrors.username ? "border-danger" : ""}`}
                      name="username"
                      placeholder="Choose a username"
                      value={registerData.username}
                      onChange={handleRegisterChange}
                    />
                    {registerErrors.username && (
                      <small className="text-danger">
                        {registerErrors.username}
                      </small>
                    )}
                  </div>

                  <div className="mb-24">
                    <label className="text-neutral-900 text-lg mb-8 fw-medium">
                      Email <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className={`common-input ${registerErrors.email ? "border-danger" : ""}`}
                      name="email"
                      placeholder="Enter your email"
                      value={registerData.email}
                      onChange={handleRegisterChange}
                    />
                    {registerErrors.email && (
                      <small className="text-danger">
                        {registerErrors.email}
                      </small>
                    )}
                  </div>

                  <div className="mb-24">
                    <label className="text-neutral-900 text-lg mb-8 fw-medium">
                      Phone Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="tel"
                      className={`common-input ${registerErrors.phone ? "border-danger" : ""}`}
                      name="phone"
                      placeholder="10-digit mobile number"
                      value={registerData.phone}
                      onChange={handleRegisterChange}
                    />
                    {registerErrors.phone && (
                      <small className="text-danger">
                        {registerErrors.phone}
                      </small>
                    )}
                  </div>

                  {/* Password Field with Eye Icon */}
                  <div className="mb-24">
                    <label className="text-neutral-900 text-lg mb-8 fw-medium">
                      Password <span className="text-danger">*</span>
                    </label>
                    <div className="position-relative">
                      <input
                        type={showRegisterPassword ? "text" : "password"}
                        className={`common-input ${registerErrors.password ? "border-danger" : ""}`}
                        name="password"
                        placeholder="Create password (min 6 characters)"
                        value={registerData.password}
                        onChange={handleRegisterChange}
                      />
                      <span
                        className={`position-absolute top-50 end-0 me-16 translate-middle-y cursor-pointer ph ${showRegisterPassword ? "ph-eye" : "ph-eye-slash"}`}
                        onClick={() =>
                          setShowRegisterPassword(!showRegisterPassword)
                        }
                      />
                    </div>
                    {registerErrors.password && (
                      <small className="text-danger">
                        {registerErrors.password}
                      </small>
                    )}
                  </div>

                  {/* Confirm Password Field with Eye Icon */}
                  <div className="mb-32">
                    <label className="text-neutral-900 text-lg mb-8 fw-medium">
                      Confirm Password <span className="text-danger">*</span>
                    </label>
                    <div className="position-relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        className={`common-input ${registerErrors.confirmPassword ? "border-danger" : ""}`}
                        name="confirmPassword"
                        placeholder="Confirm your password"
                        value={registerData.confirmPassword}
                        onChange={handleRegisterChange}
                      />
                      <span
                        className={`position-absolute top-50 end-0 me-16 translate-middle-y cursor-pointer ph ${showConfirmPassword ? "ph-eye" : "ph-eye-slash"}`}
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      />
                    </div>
                    {registerErrors.confirmPassword && (
                      <small className="text-danger">
                        {registerErrors.confirmPassword}
                      </small>
                    )}
                  </div>

                  <button
                    type="button"
                    className="btn btn-main w-100 py-16 rounded-pill fw-semibold"
                    onClick={handleSendRegistrationOtp}
                    disabled={isSendingOtp}
                  >
                    {isSendingOtp ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>{" "}
                        Sending OTP...
                      </>
                    ) : (
                      "Continue to Verify Email"
                    )}
                  </button>

                  <div className="text-center mt-24">
                    <span className="text-gray-500">
                      Already have an account?{" "}
                    </span>
                    <button
                      type="button"
                      onClick={() => setActiveForm("login")}
                      className="btn btn-link p-0 text-main-600 fw-semibold"
                    >
                      Login
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* ==================== REGISTER FORM - STEP 2: OTP VERIFICATION ==================== */}
            {activeForm === "register" && registrationStep === 2 && (
              <div className="border border-gray-100 rounded-16 px-24 py-40 bg-white shadow-sm">
                <div className="text-center mb-32">
                  <div className="w-80 h-80 bg-main-100 rounded-circle d-flex align-items-center justify-content-center mx-auto mb-24">
                    <i className="ph ph-envelope-simple text-main-600 fs-1"></i>
                  </div>
                  <h2 className="fw-semibold mb-8">Verify Your Email</h2>
                  <p className="text-gray-500">
                    We've sent a 6-digit verification code to
                    <br />
                    <strong className="text-dark">{otpData.email}</strong>
                  </p>
                </div>

                <div className="mb-24">
                  <label className="text-neutral-900 text-lg mb-8 fw-medium">
                    Enter OTP <span className="text-danger">*</span>
                  </label>
                  <div className="position-relative">
                    <input
                      type="text"
                      className={`common-input text-center fs-4 fw-bold ${otpErrors.otp ? "border-danger" : ""}`}
                      name="otp"
                      placeholder="000000"
                      value={otpData.otp}
                      onChange={handleOtpChange}
                      maxLength="6"
                      style={{ letterSpacing: "8px", fontSize: "24px" }}
                    />
                  </div>
                  {otpErrors.otp && (
                    <small className="text-danger">{otpErrors.otp}</small>
                  )}
                </div>

                <div className="d-flex justify-content-between align-items-center mb-32">
                  <span className="text-gray-500">
                    {otpTimer > 0 ? (
                      <>
                        ⏰ OTP expires in:{" "}
                        <strong className="text-danger">
                          {formatTime(otpTimer)}
                        </strong>
                      </>
                    ) : (
                      <span className="text-danger">OTP expired!</span>
                    )}
                  </span>
                  {otpTimer === 0 && (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={isSendingOtp}
                      className="btn btn-link p-0 text-main-600 fw-semibold"
                    >
                      Resend OTP
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  className="btn btn-main w-100 py-16 rounded-pill fw-semibold"
                  onClick={handleVerifyRegistrationOtp}
                  disabled={isRegisterLoading || otpData.otp.length !== 6}
                >
                  {isRegisterLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>{" "}
                      Verifying...
                    </>
                  ) : (
                    "Verify & Create Account"
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setRegistrationStep(1);
                    setOtpData({
                      otp: "",
                      email: "",
                      otpSent: false,
                      otpVerified: false,
                      tempUserId: null,
                    });
                  }}
                  className="btn btn-link w-100 mt-16 text-gray-500"
                >
                  ← Back to Registration
                </button>
              </div>
            )}

            {/* Privacy Policy Note */}
            <div className="text-center mt-32">
              <p className="text-xs text-gray-400">
                By continuing, you agree to Paudhawala's
                <Link to="/terms" className="text-main-600 mx-1">
                  Terms of Service
                </Link>
                and
                <Link to="/privacy-policy" className="text-main-600 mx-1">
                  Privacy Policy
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Account;
